import { LoadingOutlined } from '@ant-design/icons';
import { ProFormField } from '@ant-design/pro-form';
import { CharacterInput, CustomInputRenderProps, CharacterInputValue } from '@tencent/tds-ui/';
import { Modal, Form, Select, Input, SelectProps  } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { isArray, isString } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import IconClose from '@/assets/images/icons/icon-close.react.svg?react';
import { toArray } from '@/infrastructure/utils/to-array';
import { CharacterInputFormType } from '@/presentation/components/form';
import { CharacterRangePicker } from '@/presentation/components/form/standards/range';
import { MatchMode } from '@/presentation/components/form/standards/types';
import { hintStringIconText } from '@/presentation/components/hint-text/hintText';
import { stringFilterTips, versionFilterTips } from '@/presentation/components/issue-list/components/tips';
import { useStores } from '@/presentation/store';
import { ExOption, FIELD_ALL, FieldKey, FieldScope, getLocalField, Option } from '@/proto/field';
import styles from './index.module.scss';

export interface FormCharacterInputProps {
  entry: CharacterInputFormType,
  hidden?: boolean,
  form: FormInstance;
}

/**
 * 只允许单选的模式
 */
const SINGLE_INPUT_MODE = [
  MatchMode.Le,
  MatchMode.Lt,
  MatchMode.Lt,
  MatchMode.Ge,
  MatchMode.Gt,
];

/**
 * 不允许输入的模式
 */
const DISABLED_INPUT_MODE = [
  MatchMode.ISEMPTY,
  MatchMode.NOTEMPTY,
];
const { TextArea } = Input;
/**
 * 字符串类型的查询表单输入组件
 * @param props
 * @returns
 */
export const CharacterInputForm = (props: FormCharacterInputProps) => {
  const { entry, hidden, form } = props;
  const {
    name,
    label,
    labelString,
    initialValue,
    dropdownMatchSelectWidth,
    filterOption,
    // longSearch = true,
    valueSelectProps,
    modeSelectProps,
    mode,
    modeOptions,
    rangeProps,
    isSplit,
  } = entry;

  const formatAntdMode = (mode: string) => (mode !== 'single' ? mode : undefined);

  const [valueMode, setValueMode] = useState<any>(formatAntdMode(mode));

  const fieldValue = form.getFieldValue(name);
  const [matchMode, setMatchMode] = useState<MatchMode>(fieldValue?.mode);
  const [, setDropdownVisible] = useState(false);
  const fieldKey = 'fieldKey' in entry ? (entry as CharacterInputFormType).fieldKey : undefined;
  const fieldScope = 'fieldScope' in entry ? (entry as CharacterInputFormType).fieldScope : undefined;
  const userOptions = 'options' in entry ? (entry as CharacterInputFormType).options : undefined;
  const [options, setOptions] = useState<Option[] | ExOption[]>(() => (userOptions ? userOptions : [{ value: FIELD_ALL, label: '所有' }]));
  const { loadField } = useStores().FieldStore;
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [splitVisible, setSplitVisible] = useState(false);
  const ref = useRef<any>();
  const splitRef = useRef<ISplitModalRef>();

  // 输入多个数据后筛选
  const defaultFilterOption = (inputValue: string, option: any) => {
    const valueList = inputValue?.split?.(/;|,/);
    if (valueList?.length > 1) {
      return valueList?.includes?.(option.label);
    }
    return option?.label?.toLowerCase?.()?.indexOf?.(inputValue?.toLowerCase?.() || '') >= 0;
  };

  /**
   * 展开时加载
   * @param open
   */
  const onDropdownVisibleChange = async (open: boolean) => {
    setDropdownVisible(open);

    const needRemote = (fieldKey && fieldScope && !userOptions);
    if (open && needRemote && !loaded) {
      let field = getLocalField(fieldKey as FieldKey);
      if (field) {
        setOptions(field);
      } else {
        setLoading(true);
        field = await loadField(fieldKey as FieldKey, fieldScope as FieldScope);
        setOptions(field);
        setLoading(false);
      }
      setLoaded(true);
    }
  };

  const initOptions = () => {
    if (userOptions) {
      setOptions(userOptions);
      return;
    }

    const field = getLocalField(fieldKey as FieldKey);
    setOptions(field || []);
  };

  const customInputRender = useCallback((params: CustomInputRenderProps) => {
    if (matchMode === MatchMode.Between) {
      return <CharacterRangePicker
        rangeOptions={options.filter(item => !!item.value)}
        onChange={params.onChange}
        defaultValue={(fieldValue?.values && isString(fieldValue?.values)) ? [fieldValue?.values] : fieldValue?.values}
        {...rangeProps}
      />;
    }

    return params.dom;
  }, [matchMode, options, fieldValue]);

  const valueDisabled = useMemo(() => DISABLED_INPUT_MODE.includes(matchMode), [matchMode]);

  useEffect(() => {
    initOptions();
  }, [userOptions]);

  useEffect(() => {
    if (fieldValue?.mode) {
      setMatchMode(fieldValue?.mode);
    }
  }, [fieldValue]);

  useEffect(() => {
    if (MatchMode.Between === matchMode) {
      const value: CharacterInputValue = ref.current?.getValue();
      const nextValue = toArray(value?.values as string[]);
      ref.current?.onValueChange(nextValue);
      return;
    }

    if (SINGLE_INPUT_MODE.includes(matchMode)) {
      setValueMode(undefined);
      const value: CharacterInputValue = ref.current?.getValue();
      const nextValue = isArray(value?.values) ? value?.values?.[0] : value.values;
      ref.current?.onValueChange(nextValue);
    } else {
      setValueMode(formatAntdMode(mode));
    }

    if (DISABLED_INPUT_MODE.includes(matchMode)) {
      ref.current?.onValueChange(undefined);
    }
  }, [matchMode]);

  const splitValue = () => {
    setSplitVisible(true);
    const values = ref.current?.getValue()?.values;
    if (Array.isArray(values) && values.every(str => typeof(str) === 'string')) {
      splitRef?.current?.showModal(values?.join('\n'));
    } else if (typeof(values) === 'string' || !values) {
      splitRef?.current?.showModal(values);
    }
  };

  const splitCallBack = (value?: string[]) => {
    ref.current?.onValueChange?.(value);
    setSplitVisible(false);
  };

  return (
    <>
    <ProFormField
      name={name}
      label={isSplit ? hintStringIconText(
        typeof label === 'string' ? <span onClick={splitValue} style={{ cursor: 'pointer' }}>{label}</span> : (<span>{labelString || ''}</span>),
        <div>
          {name === FieldKey.StandardAppVersion ? versionFilterTips : stringFilterTips }
          <p><span onClick={splitValue} className={'underline cursor-pointer'}>点击icon进入批量输入编辑页</span>。</p>
        </div>, splitValue,
      ) : label}
      hidden={hidden}
      initialValue={initialValue}
      transform={entry.transform}
      convertValue={entry.convertValue}
    >
      <CharacterInput
        ref={ref}
        onModeChange={mode => setMatchMode(mode)}
        convert={entry.convertValue}
        modeSelectProps={{
          dropdownMatchSelectWidth: 100,
          options: modeOptions,
          ...modeSelectProps,
        }}
        customInputRender={customInputRender}
        valueSelectProps={{
          disabled: valueDisabled,
          allowClear: true,
          placeholder: '请选择',
          showSearch: true,
          mode: valueMode,
          // 过滤空选项，不需要那个所有选项了
          options: options.filter(item => !!item.value).map(item => ({
            ...item,
            ['data-tid']: `${name}-${item.label}`,
          })),
          maxTagCount: 'responsive',
          onDropdownVisibleChange,
          dropdownMatchSelectWidth,
          dropdownRender: originNode => (loading
            ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}><LoadingOutlined /></div>
            : originNode),
          dropdownClassName: 'reset-font-size',
          filterOption: filterOption ?? defaultFilterOption,
          autoClearSearchValue: true,
          // 暂时去掉分隔符支持
          // tokenSeparators: [';', ','],
          ...valueSelectProps,
        }}
      />
    </ProFormField>
      <SplitModal splitVisible={splitVisible} callBack={splitCallBack}  ref={splitRef} title={typeof label === 'string' ? label : (labelString || '')}/>
    </>
  );
};

enum SplitModel {
  Newline='0', // 换行
  Semicolon='1', // 分号
  CommaOrNewline='2'
}
interface ISplitModalProps{
  title: string;
  ref?: React.Ref<ISplitModalRef | undefined>;
  callBack: (value?: string[]) => void;
  splitVisible: boolean;
}

interface ISplitModalRef {
  showModal: (value?: string) => void;
}


const modelOptions: SelectProps['options'] = [
  { value: SplitModel.Newline, label: '使用回车进行分割' },
  { value: SplitModel.Semicolon, label: '使用英文分号进行分割' },
  { value: SplitModel.CommaOrNewline, label: '使用英文逗号或者回车进行分割' },
];

const SplitModal: React.FC<ISplitModalProps> = forwardRef((props, ref) => {
  const { title, callBack, splitVisible } = props;
  const [visible, setVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  useImperativeHandle(ref, () => ({
    showModal,
  }));

  const showModal = (value?: string) => {
    form.setFieldsValue({
      text: value,
    });
    setVisible(true);
  };
  const onCancel = () => {
    setVisible(false);
    form.resetFields();
  };
  const onOk = () => {
    const model = form.getFieldValue('model');
    const text = form.getFieldValue('text');
    switch (model) {
      case SplitModel.Newline:
        callBack(text?.split('\n')?.filter?.((str: string) => !!str));
        break;
      case SplitModel.Semicolon:
        callBack(text?.replaceAll(/\n/g, '')?.split(';')
          ?.filter?.((str: string) => !!str));
        break;
      case SplitModel.CommaOrNewline:
        callBack(text?.split?.(/\n|,/)?.filter?.((str: string) => !!str));
        break;
      default:
        break;
    }
    onCancel();
  };
  return <>
    {splitVisible ? <Modal
      width={903}
      maskClosable={false}
      visible={visible}
      title={title || '分割'}
      closeIcon={<IconClose/>}
      onCancel={onCancel}
      onOk={onOk}
      cancelButtonProps={{
        className: styles.cancelButton,
      }}
      okButtonProps={{
        style: { background: '#0A6CFF', borderRadius: '3px' },
      }}
      className={styles.modal}>
      <Form form={form} className={styles.form}>
        <Form.Item name={'model'} initialValue={SplitModel.Newline}>
          <Select className={'w-full'} options={modelOptions}/>
        </Form.Item>
        <Form.Item name={'text'} noStyle>
          <TextArea rows={10}/>
        </Form.Item>
      </Form>
    </Modal> : null}
  </>;
});
